import axios from 'axios';
import { store } from '../app/store';
import axiosRetry from 'axios-retry';
import { auth } from '../app/firebase';
import { setAccessToken } from '../features/auth/authSlice';
import {
    BLOCK_CLIENT,
    BLOCK_CRAFTSMAN,
    CHATS,
    CLIENTS_ACTIVE,
    CLIENTS_BLOCKED,
    CONSULTATIONS_FOR_REQUEST,
    CRAFTSMAN_APPROVED,
    CRAFTSMAN_BLOCKED,
    CRAFTSMAN_PENDING,
    DOWNLOAD_FILE,
    GET_CLIENT_BY_ID,
    GET_CRAFTSMAN_BY_ID,
    GET_REQUEST_BY_ID,
    LOGIN_ADMIN,
    REQUESTS_APPROVED,
    REQUESTS_ARCHIVED,
    REQUESTS_REQUESTED,
    UNBLOCK_CLIENT,
    UNBLOCK_CRAFTSMAN,
    IS_ADMIN,
    CREATE_STORE,
    GET_STORES,
    GET_STORE_DETAILS,
    GET_STORE_CRAFTSMAN,
    GET_ALL_CRAFTSMAN,
    UPDATE_STORE_CRAFTSMAN,
    GET_USER_CREDITS,
    REQUESTS_CANCELED,
    GET_CREDITS_HISTORY,
    DELETE_USER,
    DELETE_CRAFTSMAN
} from './constants';
import { firebaseSignOut } from '../features/auth/authAPI';
let retryCounter = 0;

export const HttpService = axios.create({
    timeout: 5000,
    baseURL: 'https://d.infn.dev/diy/api',
});

export const getNewToken = async () => {
    const getToken = async () =>
        await (
            await auth.currentUser.getIdTokenResult(true)
        ).token;
    const accessToken = await getToken();
    store.dispatch(setAccessToken(accessToken));
    console.log('[Access Token]: refreshed');
};

HttpService.interceptors.request.use((config) => {
    const token = store.getState().auth.accessToken;
    config.headers['accessToken'] = token;
    return config;
});

HttpService.interceptors.response.use(
    (response) => response,
    async (error) => {
        console.log(error);
        if (error.response.status === 401 || error.response.status === 400) {
            // Refresh the JWT and retry the failed request
            // JWT lives 1h
            retryCounter--; // just in case to prevent loop
            if (retryCounter > 0) {
                await getNewToken();
                return HttpService.request(error.config);
            }
        }
        return Promise.reject(error);
    }
);

axiosRetry(HttpService, {
    retries: 1,
    retryDelay: (retryCount) => retryCount * 1000,
    shouldRetry: (error) => {
        // Check if the error status code indicates an expired token
        return error.response?.status === 400 || error.response?.status === 401;
    },
    retryCondition: (error) => {
        return error.response?.status === 400 || error.response?.status === 401;
    },
});

export const API = {
    login: async () => {
        return await HttpService.get(LOGIN_ADMIN);
    },
    isAdmin: async (data) => {
        return await HttpService.post(IS_ADMIN, data);
    },
    getRequests_requested: async (query) => {
        return await HttpService.get(REQUESTS_REQUESTED(query));
    },
    getRequests_approved: async (query) => {
        return await HttpService.get(REQUESTS_APPROVED(query));
    },
    getRequests_archived: async (query) => {
        return await HttpService.get(REQUESTS_ARCHIVED(query));
    },
    getRequests_canceled: async (query) => {
        return await HttpService.get(REQUESTS_CANCELED(query));
    },
    getCraftsman_pending: async (query) => {
        return await HttpService.get(CRAFTSMAN_PENDING(query));
    },
    getCraftsman_blocked: async (query) => {
        return await HttpService.get(CRAFTSMAN_BLOCKED(query));
    },
    getCraftsman_approved: async (query) => {
        return await HttpService.get(CRAFTSMAN_APPROVED(query));
    },
    getClients_active: async (query) => {
        return await HttpService.get(CLIENTS_ACTIVE(query));
    },
    getClients_blocked: async (query) => {
        return await HttpService.get(CLIENTS_BLOCKED(query));
    },
    getClientById: async (id) => {
        return await HttpService.get(GET_CLIENT_BY_ID(id));
    },
    getCraftsmanById: async (id) => {
        return await HttpService.get(GET_CRAFTSMAN_BY_ID(id));
    },
    blockClient: async (id) => {
        return await HttpService.get(BLOCK_CLIENT(id));
    },
    unblockClient: async (id) => {
        return await HttpService.get(UNBLOCK_CLIENT(id));
    },
    blockCraftsman: async (id) => {
        return await HttpService.get(BLOCK_CRAFTSMAN(id));
    },
    unblockCraftsman: async (id) => {
        return await HttpService.get(UNBLOCK_CRAFTSMAN(id));
    },
    getRequestById: async (id) => {
        return await HttpService.get(GET_REQUEST_BY_ID(id));
    },
    downloadFileByName: async (filename) => {
        return await HttpService.get(DOWNLOAD_FILE(filename), {
            headers: {
                accept: 'application/octet-stream',
            },
            responseType: 'blob',
        });
    },
    getConsultationsForRequest: async (id) => {
        return await HttpService.get(CONSULTATIONS_FOR_REQUEST(id));
    },
    getAdminChats: async (ids) => {
        return await HttpService.post(CHATS, ids);
    },
    createStore: async (data) => {
        return await HttpService.post(CREATE_STORE, data);
    },
    editStore: async (data) => {
        return await HttpService.put(CREATE_STORE, data);
    },
    getStores: async () => {
        const res = await HttpService.get(GET_STORES);
        return res.data;
    },
    getStoreDetails: async (id) => {
        const res = await HttpService.get(GET_STORE_DETAILS(id));
        return res.data;
    },
    getAllCraftsman: async () => {
        const res = await HttpService.get(GET_ALL_CRAFTSMAN);
        return res.data;
    },
    getStoreCraftsman: async (id) => {
        const res = await HttpService.get(GET_STORE_CRAFTSMAN(id));
        return res.data;
    },
    updateStoreCraftsman: async (id, data) => {
        return await HttpService.put(UPDATE_STORE_CRAFTSMAN(id), data);
    },
    getUserCredits: async (id) => {
        const res = await HttpService.get(GET_USER_CREDITS(id));
        return res.data;
    },
    downloadCreditsHistory: async (userId) => {
        const res = await HttpService.get(GET_CREDITS_HISTORY(userId), {
            responseType: 'blob',
        });
        return res.data;
    },
    deleteUser: (id) => {
        return HttpService.delete(DELETE_USER(id));
    },
    deleteCraftsman : (id) => {
        return HttpService.delete(DELETE_CRAFTSMAN(id));

    }
};

export async function checkUser(uid) {
    try {
        if (uid) {
            const resp = await (await API.login()).data;
            if (resp) {
                const user = auth.currentUser;
                return { ...resp, ...user };
            } else {
                firebaseSignOut();
                return false;
            }
        } else {
            firebaseSignOut();
            return false;
        }
    } catch (error) {
        console.log(error);
        return error;
    }
}

export const getImage = async (filename) => {
    if (!filename) {
        return;
    }
    try {
        const { data } = await API.downloadFileByName(filename);
        const objectURL = URL.createObjectURL(data);
        return objectURL;
    } catch (error) {
        console.log(error);
        return false;
    }
};
